/**
 * Custom Product Highlight — Shop the Look
 *
 * Manages hotspot dot toggle, product card positioning
 * with boundary detection, outside-click / escape close,
 * mobile bottom-sheet, and quick-add modal coordination.
 */
(function () {
  'use strict';

  var MOBILE_BP = 750;
  var CARD_W = 260;
  var PAD = 8;
  var GAP = 12;

  function isMobile() {
    return window.innerWidth < MOBILE_BP;
  }

  /**
   * Position the card within the container near the dot.
   * Uses the merchant's preferred direction but falls back
   * if there isn't enough space, then clamps to stay in bounds.
   */
  function positionCard(card, dot, container, direction) {
    if (isMobile()) return;

    var cRect = container.getBoundingClientRect();
    var dRect = dot.getBoundingClientRect();

    var dotX = dRect.left - cRect.left + dRect.width / 2;
    var dotY = dRect.top - cRect.top + dRect.height / 2;
    var dotR = dRect.width / 2;

    // Measure card height
    card.style.visibility = 'hidden';
    card.style.display = 'block';
    card.style.top = '0';
    card.style.left = '0';
    var cardH = card.offsetHeight;
    card.style.display = '';
    card.style.visibility = '';

    var spaceAbove = dotY - dotR - GAP;
    var spaceBelow = cRect.height - dotY - dotR - GAP;
    var spaceLeft = dotX - dotR - GAP;
    var spaceRight = cRect.width - dotX - dotR - GAP;

    var top, left;

    if (direction === 'left' || direction === 'right') {
      // Horizontal: try preferred side, fallback to other, fallback to vertical
      if (direction === 'left' && spaceLeft >= CARD_W) {
        left = dotX - dotR - GAP - CARD_W;
        top = dotY - cardH / 2;
      } else if (direction === 'right' && spaceRight >= CARD_W) {
        left = dotX + dotR + GAP;
        top = dotY - cardH / 2;
      } else if (spaceRight >= CARD_W) {
        left = dotX + dotR + GAP;
        top = dotY - cardH / 2;
      } else if (spaceLeft >= CARD_W) {
        left = dotX - dotR - GAP - CARD_W;
        top = dotY - cardH / 2;
      } else {
        // Fall back to vertical
        left = dotX - CARD_W / 2;
        if (spaceBelow >= cardH) {
          top = dotY + dotR + GAP;
        } else {
          top = dotY - dotR - GAP - cardH;
        }
      }
    } else {
      // Vertical: try preferred side, fallback to other, fallback to horizontal
      if (direction === 'top' && spaceAbove >= cardH) {
        top = dotY - dotR - GAP - cardH;
        left = dotX - CARD_W / 2;
      } else if (direction === 'bottom' && spaceBelow >= cardH) {
        top = dotY + dotR + GAP;
        left = dotX - CARD_W / 2;
      } else if (spaceBelow >= cardH) {
        top = dotY + dotR + GAP;
        left = dotX - CARD_W / 2;
      } else if (spaceAbove >= cardH) {
        top = dotY - dotR - GAP - cardH;
        left = dotX - CARD_W / 2;
      } else {
        // Fall back to horizontal
        top = dotY - cardH / 2;
        if (spaceRight >= CARD_W) {
          left = dotX + dotR + GAP;
        } else {
          left = dotX - dotR - GAP - CARD_W;
        }
      }
    }

    // Clamp within container
    top = Math.max(PAD, Math.min(top, cRect.height - cardH - PAD));
    left = Math.max(PAD, Math.min(left, cRect.width - CARD_W - PAD));

    card.style.top = top + 'px';
    card.style.left = left + 'px';
  }

  function init(section) {
    var hotspots = section.querySelectorAll('[data-hotspot]');
    var container = section.querySelector('.custom-product-highlight__hotspots');
    var backdrop = section.querySelector('[data-backdrop]');

    if (!hotspots.length || !container) return;

    function getCard(index) {
      return container.querySelector('[data-hotspot-card="' + index + '"]');
    }

    function closeActive(returnFocus) {
      var active = section.querySelector('[data-hotspot][data-active]');
      if (!active) return;

      var idx = active.getAttribute('data-hotspot-index');
      var dot = active.querySelector('[data-hotspot-toggle]');
      var card = getCard(idx);

      active.removeAttribute('data-active');
      dot.setAttribute('aria-expanded', 'false');
      if (card) card.removeAttribute('data-active');

      if (backdrop) backdrop.removeAttribute('data-visible');
      if (isMobile()) {
        document.body.classList.remove('overflow-hidden');
      }

      if (returnFocus && dot) dot.focus();
    }

    function openHotspot(hotspot) {
      closeActive(false);

      var idx = hotspot.getAttribute('data-hotspot-index');
      var dot = hotspot.querySelector('[data-hotspot-toggle]');
      var card = getCard(idx);
      var direction = hotspot.getAttribute('data-direction') || 'top';

      hotspot.setAttribute('data-active', '');
      dot.setAttribute('aria-expanded', 'true');

      if (card) {
        if (!isMobile()) {
          positionCard(card, dot, container, direction);
        }
        card.setAttribute('data-active', '');
      }

      if (isMobile()) {
        if (backdrop) backdrop.setAttribute('data-visible', '');
        document.body.classList.add('overflow-hidden');
      }
    }

    hotspots.forEach(function (hotspot) {
      var dot = hotspot.querySelector('[data-hotspot-toggle]');
      if (!dot) return;

      dot.addEventListener('click', function (e) {
        e.stopPropagation();
        if (hotspot.hasAttribute('data-active')) {
          closeActive(false);
        } else {
          openHotspot(hotspot);
        }
      });
    });

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeActive(true);
      });
    }

    document.addEventListener('click', function (e) {
      var active = section.querySelector('[data-hotspot][data-active]');
      if (!active) return;
      var idx = active.getAttribute('data-hotspot-index');
      var card = getCard(idx);
      if (active.contains(e.target)) return;
      if (card && card.contains(e.target)) return;
      if (e.target.closest('.quick-add-modal')) return;
      closeActive(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var active = section.querySelector('[data-hotspot][data-active]');
        if (active) {
          e.preventDefault();
          closeActive(true);
        }
      }
    });

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          if (mutation.target.hasAttribute('open')) {
            closeActive(false);
          }
        }
      });
    });

    requestAnimationFrame(function () {
      document.querySelectorAll('quick-add-modal').forEach(function (modal) {
        observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
      });
    });

    return function destroy() {
      observer.disconnect();
    };
  }

  function initAll() {
    document.querySelectorAll('.custom-product-highlight').forEach(function (s) {
      init(s);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('.custom-product-highlight');
    if (s) init(s);
  });
})();
